import { AquaApi, AquaApiError, AquaTree, FieldDefinition, TemplateLite } from '../api'
import { el, clear, downloadJson } from '../dom'

/**
 * Coerce a raw HTML-input string to the JSON type the template schema expects.
 *
 * HTML inputs always yield strings, but the server validates form payloads
 * against the template's JSON Schema. A `number` field (JSON Schema `number`
 * OR `integer` — both map to field_type "number") must be sent as a real JSON
 * number, otherwise validation fails with e.g. `"212121" is not of type
 * "integer"`. A whole-number JS value serializes without a decimal point, so it
 * satisfies both `number` and `integer` schemas. Non-numeric input falls back
 * to the original string so the server returns a meaningful validation error.
 */
export function coerceFieldValue(fieldType: string, raw: string): unknown {
  if (fieldType === 'number') {
    const n = Number(raw)
    return Number.isFinite(n) ? n : raw
  }
  return raw
}

/**
 * Read a single form input into the value to submit for its field.
 *
 * A checkbox reports its state via `.checked` (a real boolean) and is always
 * included — an unchecked box is a meaningful `false`. Every other input
 * reports a string `.value`: empty strings are omitted, and the value is
 * coerced to the JSON type the template schema expects (see
 * {@link coerceFieldValue}). Returning `include: false` lets the caller skip a
 * field rather than send an empty string the server would reject.
 */
export function readFieldValue(
  fieldType: string,
  input: HTMLInputElement | HTMLSelectElement,
): { include: boolean; value: unknown } {
  if (input instanceof HTMLInputElement && input.type === 'checkbox') {
    return { include: true, value: input.checked }
  }
  if (input.value === '') return { include: false, value: undefined }
  return { include: true, value: coerceFieldValue(fieldType, input.value) }
}

export interface AquafyTabOptions {
  api: AquaApi
  defaultTemplate?: string
  onAquafied: (detail: unknown) => void
  onError: (err: AquaApiError) => void
}

export class AquafyTab {
  readonly root = el('div', { class: 'aquafy' })

  constructor(private o: AquafyTabOptions) {
    this.renderChooser()
  }

  private showError(e: unknown) {
    const err =
      e instanceof AquaApiError
        ? e
        : new AquaApiError(0, String((e as Error).message))
    this.root.append(el('div', { class: 'err' }, err.message))
    this.o.onError(err)
  }

  /** Top-level: pick file aquafication or a template form. */
  private renderChooser() {
    clear(this.root)
    this.root.append(
      this.fileDrop(),
      el('div', { class: 'row' }, el('span', { class: 'muted' }, 'or fill a template form:')),
      this.templatePicker(),
    )
    if (this.o.defaultTemplate) this.loadTemplate(this.o.defaultTemplate)
  }

  private fileDrop(): HTMLElement {
    const drop = el('div', { class: 'drop' }, 'Drop a file here, or click to choose')
    const input = el('input', { type: 'file' }) as HTMLInputElement
    input.style.display = 'none'
    drop.addEventListener('click', () => input.click())
    drop.addEventListener('dragover', (e) => {
      e.preventDefault()
      drop.classList.add('over')
    })
    drop.addEventListener('dragleave', () => drop.classList.remove('over'))
    drop.addEventListener('drop', (e) => {
      e.preventDefault()
      drop.classList.remove('over')
      const f = (e as DragEvent).dataTransfer?.files?.[0]
      if (f) this.aquafyFile(f)
    })
    input.addEventListener('change', () => {
      const f = input.files?.[0]
      if (f) this.aquafyFile(f)
    })
    const wrap = el('div', {}, drop, input)
    return wrap
  }

  private async aquafyFile(file: File) {
    clear(this.root)
    this.root.append(el('div', { class: 'muted' }, `Aquafying ${file.name}…`))
    try {
      const res = await this.o.api.aquafyFile(file)
      this.renderResult(`${file.name}.aqua.json`, res.aqua_tree, res.file_hash)
      this.o.onAquafied(res)
    } catch (e) {
      this.renderChooser()
      this.showError(e)
    }
  }

  private renderResult(filename: string, tree: unknown, fileHash: string) {
    clear(this.root)
    this.root.append(
      el('div', { class: 'badge valid' }, 'Aquafied ✓'),
      el('div', { class: 'muted' }, `content hash: ${fileHash}`),
      el('div', { class: 'row' },
        el('button', { class: 'act' }, 'Download .aqua.json'),
        el('button', { class: 'act ghost' }, 'Aquafy another'),
      ),
    )
    // Wire up after creation to avoid closure issues with typed onclick attrs
    const btns = this.root.querySelectorAll('button')
    btns[0]?.addEventListener('click', () => downloadJson(filename, tree))
    btns[1]?.addEventListener('click', () => this.renderChooser())
  }

  // ---- Template form + selective disclosure ----

  private templatePicker(): HTMLElement {
    const select = el('select', {}) as HTMLSelectElement
    select.append(el('option', { value: '' }, 'Select a template…'))
    this.o.api
      .listTemplates()
      .then(({ templates }) =>
        templates.forEach((t) =>
          select.append(el('option', { value: t.id }, t.title)),
        ),
      )
      .catch((e) => this.showError(e))
    select.addEventListener('change', () => {
      if (select.value) this.loadTemplate(select.value)
    })
    return el('div', { class: 'field' }, select)
  }

  private async loadTemplate(id: string) {
    try {
      const { template } = await this.o.api.getTemplate(id)
      this.renderForm(template)
    } catch (e) {
      this.showError(e)
    }
  }

  private renderForm(t: TemplateLite) {
    clear(this.root)
    const inputs = new Map<string, HTMLInputElement | HTMLSelectElement>()
    const fieldNodes = t.fields.map((f) => this.fieldInput(f, inputs))
    const submit = el('button', { class: 'act' }, 'Aquafy form')
    const cancel = el('button', { class: 'act ghost' }, 'Cancel')
    submit.addEventListener('click', () => this.submitForm(t, inputs))
    cancel.addEventListener('click', () => this.renderChooser())
    this.root.append(
      el('div', { class: 'muted' }, t.title),
      ...fieldNodes,
      el('div', { class: 'row' }, submit, cancel),
    )
  }

  private fieldInput(
    f: FieldDefinition,
    inputs: Map<string, HTMLInputElement | HTMLSelectElement>,
  ): HTMLElement {
    let input: HTMLInputElement | HTMLSelectElement
    if (f.field_type === 'boolean' || f.field_type === 'checkbox') {
      input = el('input', { type: 'checkbox' }) as HTMLInputElement
    } else if (f.field_type === 'radio' && f.options?.length) {
      const sel = el('select', {}) as HTMLSelectElement
      f.options.forEach((o) => sel.append(el('option', { value: o }, o)))
      input = sel
    } else {
      const type =
        f.field_type === 'date'
          ? 'date'
          : f.field_type === 'number'
            ? 'number'
            : 'text'
      input = el('input', { type, placeholder: f.label }) as HTMLInputElement
    }
    inputs.set(f.name, input)
    return el('div', { class: 'field' }, el('label', {}, f.label + (f.required ? ' *' : '')), input)
  }

  private async submitForm(
    t: TemplateLite,
    inputs: Map<string, HTMLInputElement | HTMLSelectElement>,
  ) {
    const typeByName = new Map(t.fields.map((f) => [f.name, f.field_type]))
    const payloads: Record<string, unknown> = {}
    for (const [name, input] of inputs) {
      const { include, value } = readFieldValue(typeByName.get(name) ?? 'text', input)
      if (include) payloads[name] = value
    }
    try {
      const { aqua_tree } = await this.o.api.aquafyForm(t.id, payloads)
      this.o.onAquafied({ aqua_tree, template: t.id })
      this.renderDisclosure(t, aqua_tree, payloads)
    } catch (e) {
      this.showError(e)
    }
  }

  /**
   * Pick the best revision hash for redaction.
   *
   * The form tree from `aquafy-form` may contain multiple revisions (e.g. a
   * Template genesis, an Anchor, and the typed Object revision). The redact
   * endpoint requires the tree-method object revision; scalar revisions will
   * cause a server-side error.
   *
   * Strategy (defensive — does not crash on unexpected shapes):
   *   1. If only one revision exists, use it (nothing to choose).
   *   2. Among all revisions, prefer the one whose value carries
   *      `"method": "tree"` (indicating a tree-method / typed revision).
   *   3. If no revision has method "tree", fall back to the last key
   *      (the object is typically appended last by the server).
   */
  private pickRevisionHash(tree: AquaTree): string {
    const keys = Object.keys(tree.revisions)
    if (keys.length === 0) return ''
    if (keys.length === 1) return keys[0]

    // Look for a revision with method === "tree"
    for (const key of keys) {
      const rev = tree.revisions[key]
      if (
        rev !== null &&
        typeof rev === 'object' &&
        'method' in rev &&
        (rev as Record<string, unknown>)['method'] === 'tree'
      ) {
        return key
      }
    }

    // Fallback: last key (object is typically appended last)
    return keys[keys.length - 1]
  }

  /** Selective-disclosure panel: list every field with a mute toggle (default muted). */
  private renderDisclosure(
    t: TemplateLite,
    tree: AquaTree,
    payloads: Record<string, unknown>,
  ) {
    clear(this.root)

    const revisionHash = this.pickRevisionHash(tree)
    const reveal = new Map<string, HTMLInputElement>() // field name -> "reveal" checkbox

    const rows = t.fields
      .filter((f) => f.name in payloads)
      .map((f) => {
        const cb = el('input', { type: 'checkbox' }) as HTMLInputElement // unchecked = muted
        reveal.set(f.name, cb)
        return el(
          'div',
          { class: 'mute' },
          cb,
          el('span', {}, f.label),
          el('span', { class: 'val' }, String(payloads[f.name])),
        )
      })

    const out = el('div', {})
    const make = el('button', { class: 'act' }, 'Create shareable version')
    make.addEventListener('click', async () => {
      const disclosed = [...reveal.entries()]
        .filter(([, cb]) => cb.checked)
        .map(([name]) => `/payloads/${name}`)
      try {
        const { selective_tree } = await this.o.api.redact(tree, revisionHash, disclosed)
        clear(out)
        const dlBtn = el('button', { class: 'act' }, 'Download')
        dlBtn.addEventListener('click', () =>
          downloadJson(`${t.title}.redacted.aqua.json`, selective_tree),
        )
        out.append(
          el('div', { class: 'badge valid' }, 'Shareable version ready ✓'),
          el('div', { class: 'row' }, dlBtn),
        )
      } catch (e) {
        this.showError(e)
      }
    })

    const dlFull = el('button', { class: 'act ghost' }, 'Download full')
    dlFull.addEventListener('click', () =>
      downloadJson(`${t.title}.full.aqua.json`, tree),
    )

    this.root.append(
      el('div', { class: 'muted' }, 'Tick the fields to reveal. Unticked fields stay muted (redacted).'),
      ...rows,
      el('div', { class: 'row' }, make, dlFull),
      out,
    )
  }
}
